import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import Loader from 'src/components/Loader/Loader';
import { RootState } from 'src/store';
import { readQueryParams } from 'src/utils/QueryParamsHelper';
import { TEAM_LEAD_COLUMN_DEFS_NAVI_TABLE } from './TableContent';
import TeamLeadFilters from './TeamLeadFilters';
import { TABLE_NAME, defaultQueryParam, queryParam } from './interface';
import { getTeamLeadDashboard, getTeamLeadDashboardFilters } from './teamLeadActions';
import { formatNumber, navigateToCaseDetails } from 'src/utils/commonUtils';
import APP_ROUTES from 'src/layout/Routes';
import cx from 'classnames';
import AnimateWithLottie from '@cp/src/components/AnimationWithLottie';
import axios from 'axios';
import { logError } from '@cp/src/utils/ApiHelper';
import NaviTable from '@cp/src/components/NaviTable/NaviTable';
import styles from './TeamLead.module.scss';

export const getQueryString = () => {
  const queryParams = readQueryParams();
  return queryParams[TABLE_NAME] as queryParam;
};

export const TeamLeadDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const gridEvent = useRef<any>(null);
  const [isFilterApplied, setIsFilterApplied] = React.useState<boolean>(false);
  const user = useSelector((state: RootState) => state.common.userData);
  const currentPageData = useSelector((state: RootState) => state.teamLeadDashboard.teamLeadData);
  const page = useSelector((state: RootState) => state.teamLeadDashboard.page);
  const loading = useSelector((state: RootState) => state.teamLeadDashboard.loading);
  const isLonghornThemeEnabled = useSelector(
    (state: RootState) => state.common.isLonghornThemeEnabled
  );

  const lottieFile = useSelector((store: RootState) => store?.longhornTheme?.assets?.lottieFileUrl);
  const [animationData, setAnimationData] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (lottieFile) {
          const response = await axios.get(lottieFile);
          setAnimationData(response.data);
        }
      } catch (error) {
        logError(error, 'Error fetching lottie file');
      }
    };
    fetchData();
  }, [lottieFile]);

  useEffect(() => {
    if (!user) return;
    dispatch(getTeamLeadDashboardFilters({ agencyCode: user?.agencyCode }));
  }, [user?.agencyCode]);

  useEffect(() => {
    const queryString = getQueryString();
    const defaultParams = {
      ...defaultQueryParam,
      ...queryString,
      pageNo: queryString?.pageIndex || '0',
      agencyCode: user?.agencyCode
    };
    dispatch(getTeamLeadDashboard(defaultParams));
    if (typeof gridEvent.current?.sizeColumnsToFit === 'function') {
      gridEvent.current?.sizeColumnsToFit();
    }
  }, [location]);

  const onCasesRowClicked = (data: any) => {
    navigateToCaseDetails(
      data?.customerId,
      data?.accountNumber,
      data?.caseReferenceId,
      data?.businessVertical,
      navigate,
      APP_ROUTES.CASE_DETAIL.path
    );
  };

  return (
    <div className={cx(styles.teamLeadContainer, 'teamLeadContainer')}>
      <div
        className={cx({
          [styles.caseCountContainer]: true,
          ['!mb-[16px]']: isLonghornThemeEnabled
        })}
      >
        <div className={styles.casesCount}>
          {formatNumber(page?.totalElements ?? 0)} Allocations
        </div>
        {isLonghornThemeEnabled && !isFilterApplied ? (
          <AnimateWithLottie animationData={animationData} />
        ) : null}
        <TeamLeadFilters
          onFilteredApplied={() => setIsFilterApplied(true)}
          onFilterCleared={() => setIsFilterApplied(false)}
        />
      </div>
      <NaviTable
        tableName={TABLE_NAME}
        columns={TEAM_LEAD_COLUMN_DEFS_NAVI_TABLE}
        data={currentPageData || []}
        totalElements={page?.totalElements || 0}
        loading={loading}
        enableRowClick={() => true}
        onRowClick={onCasesRowClicked}
        defaultPaginationState={{
          pageIndex: 0,
          pageSize: 50
        }}
      />
      <Loader show={loading} className={'overlay'} animate={false} />
    </div>
  );
};

export default TeamLeadDashboard;
